import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, Package, Minus, Plus, ArrowLeft, AlertTriangle, ShoppingBag, User, Mail, Phone, MapPin, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ExtensionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ext, setExt] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', delivery_method: 'retrait' });
  const [submitting, setSubmitting] = useState(false);
  const [wishlistEmail, setWishlistEmail] = useState(localStorage.getItem('wishlist_email') || '');
  const [wishlistAdded, setWishlistAdded] = useState(false);

  useEffect(() => {
    api.get(`/extensions/${id}`).then(r => setExt(r.data)).catch(() => toast.error("Extension introuvable"));
  }, [id]);

  if (!ext) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-violet-600 border-t-transparent rounded-full" /></div>;

  const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  const isClosed = ext.status !== 'ouvert';
  const selectedItems = Object.entries(quantities).filter(([, q]) => q > 0);
  const total = selectedItems.reduce((s, [itemId, qty]) => {
    const item = ext.items.find(i => i.id === itemId);
    return s + (item ? item.price * qty : 0);
  }, 0);

  const updateQty = (itemId, delta, limit) => {
    setQuantities(prev => {
      const current = prev[itemId] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [itemId]: limit ? Math.min(next, limit) : next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error("Veuillez remplir tous les champs"); return;
    }
    setSubmitting(true);
    try {
      const items = selectedItems.map(([item_id, quantity]) => ({ item_id, quantity }));
      const res = await api.post('/preorders', { extension_id: id, ...form, items });
      toast.success("Précommande enregistrée !");
      navigate(`/confirmation/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la précommande");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-8 md:py-12">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-5xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 text-slate-500 hover:text-slate-700 rounded-full -ml-3" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Retour
        </Button>

        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              {ext.image_url ? (
                <img src={ext.image_url} alt={ext.name} className="w-full h-64 object-cover rounded-2xl mb-6" />
              ) : (
                <div className="w-full h-64 bg-gradient-to-br from-violet-50 to-amber-50 rounded-2xl mb-6 flex items-center justify-center">
                  <Package className="h-16 w-16 text-violet-200" />
                </div>
              )}
              <div className="flex flex-wrap gap-2 mb-3">
                {ext.product_type === 'tcg' && ext.tcg_name && <Badge className="bg-violet-100 text-violet-700 border-violet-200">{ext.tcg_name}</Badge>}
                {ext.product_type === 'jeu_de_societe' && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Jeu de société</Badge>}
                {ext.product_type === 'bd_manga' && <Badge className="bg-rose-100 text-rose-700 border-rose-200">BD & Manga</Badge>}
                <Badge className={ext.status === 'ouvert' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-rose-100 text-rose-800 border-rose-200'}>
                  {ext.status === 'ouvert' ? 'Précommandes ouvertes' : 'Précommandes fermées'}
                </Badge>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" data-testid="extension-name">{ext.name}</h1>
              {ext.description && <p className="text-slate-600 mt-3 leading-relaxed">{ext.description}</p>}
              <div className="flex flex-wrap gap-6 mt-5 text-sm text-slate-500">
                <div className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> Sortie : {formatDate(ext.release_date)}</div>
                <div className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> Limite : {formatDate(ext.preorder_deadline)}</div>
              </div>

              {!isClosed && (
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">Les quantités finales sont susceptibles d'ajustement en fonction des allocations reçues du fournisseur.</p>
                </div>
              )}

              <Separator className="my-8" />
              <h2 className="text-xl font-bold text-slate-900 mb-6" data-testid="items-heading">
                {ext.product_type === 'jeu_de_societe' ? 'Produit' : 'Produits disponibles'}
              </h2>
              <div className="space-y-4">
                {ext.items.map(item => (
                  <div key={item.id} className="bg-white border border-slate-200 rounded-xl p-5 hover:border-violet-200 transition-colors" data-testid={`item-card-${item.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      {item.image_url && (
                        <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-lg overflow-hidden border border-slate-100 shrink-0 bg-slate-50">
                          <img src={item.image_url} alt={item.name} className="h-full w-full object-cover" data-testid={`item-image-${item.id}`} />
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{item.name}</h3>
                        {item.description && <p className="text-sm text-slate-500 mt-1">{item.description}</p>}
                        <p className="text-lg font-bold text-violet-600 mt-2">{item.price.toFixed(2)} €</p>
                        {item.limit_per_client && <p className="text-xs text-slate-400 mt-1">Limité à {item.limit_per_client} par client</p>}
                      </div>
                      {!isClosed && (
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg" onClick={() => updateQty(item.id, -1, item.limit_per_client)} data-testid={`item-minus-${item.id}`}>
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-10 text-center font-semibold text-lg" data-testid={`item-qty-${item.id}`}>{quantities[item.id] || 0}</span>
                          <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg" onClick={() => updateQty(item.id, 1, item.limit_per_client)} data-testid={`item-plus-${item.id}`}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-2">
            <div className="sticky top-24">
              {step === 1 && !isClosed && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="order-summary">
                  <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2"><ShoppingBag className="h-5 w-5 text-violet-600" /> Votre sélection</h3>
                  <Separator className="my-4" />
                  {selectedItems.length === 0 ? (
                    <p className="text-sm text-slate-400 py-4">Sélectionnez des produits à gauche</p>
                  ) : (
                    <>
                      <div className="space-y-3">
                        {selectedItems.map(([itemId, qty]) => {
                          const item = ext.items.find(i => i.id === itemId);
                          return item ? (
                            <div key={itemId} className="flex justify-between text-sm">
                              <span className="text-slate-700">{item.name} x{qty}</span>
                              <span className="font-medium text-slate-900">{(item.price * qty).toFixed(2)} €</span>
                            </div>
                          ) : null;
                        })}
                      </div>
                      <Separator className="my-4" />
                      <div className="flex justify-between font-bold text-lg">
                        <span>Total</span>
                        <span className="text-violet-600">{total.toFixed(2)} €</span>
                      </div>
                      <Button className="w-full mt-6 rounded-full py-3" onClick={() => setStep(2)} data-testid="continue-btn">
                        Continuer
                      </Button>
                    </>
                  )}
                </motion.div>
              )}

              {step === 2 && !isClosed && (
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="preorder-form">
                  <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="mb-4 -ml-2 text-slate-500 rounded-full" data-testid="form-back-btn">
                    <ArrowLeft className="h-4 w-4 mr-1" /> Modifier la sélection
                  </Button>
                  <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2"><User className="h-5 w-5 text-violet-600" /> Vos coordonnées</h3>
                  <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="first_name" className="text-xs font-medium text-slate-500">Prénom</Label>
                        <Input id="first_name" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} className="mt-1 rounded-lg" required data-testid="input-firstname" />
                      </div>
                      <div>
                        <Label htmlFor="last_name" className="text-xs font-medium text-slate-500">Nom</Label>
                        <Input id="last_name" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} className="mt-1 rounded-lg" required data-testid="input-lastname" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-xs font-medium text-slate-500 flex items-center gap-1"><Mail className="h-3 w-3" /> Email</Label>
                      <Input id="email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="mt-1 rounded-lg" required data-testid="input-email" />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-xs font-medium text-slate-500 flex items-center gap-1"><Phone className="h-3 w-3" /> Téléphone</Label>
                      <Input id="phone" type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="mt-1 rounded-lg" required data-testid="input-phone" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-500 flex items-center gap-1 mb-2"><MapPin className="h-3 w-3" /> Mode de retrait</Label>
                      <RadioGroup value={form.delivery_method} onValueChange={(v) => setForm(f => ({ ...f, delivery_method: v }))} data-testid="delivery-radio">
                        <div className="flex items-center gap-2 p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                          <RadioGroupItem value="retrait" id="retrait" />
                          <Label htmlFor="retrait" className="cursor-pointer text-sm">Retrait en boutique</Label>
                        </div>
                        <div className="flex items-center gap-2 p-3 border rounded-lg hover:bg-slate-50 transition-colors mt-2">
                          <RadioGroupItem value="expedition" id="expedition" />
                          <Label htmlFor="expedition" className="cursor-pointer text-sm">Expédition</Label>
                        </div>
                      </RadioGroup>
                    </div>
                    <Separator />
                    <div className="flex justify-between font-bold">
                      <span>Total</span>
                      <span className="text-violet-600">{total.toFixed(2)} €</span>
                    </div>
                    <Button type="submit" className="w-full rounded-full py-3" disabled={submitting} data-testid="submit-preorder-btn">
                      {submitting ? 'Envoi en cours...' : 'Confirmer la précommande'}
                    </Button>
                  </form>
                </motion.div>
              )}

              {isClosed && (
                <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-center">
                  <AlertTriangle className="h-8 w-8 text-rose-400 mx-auto" />
                  <p className="mt-3 font-semibold text-rose-800">Précommandes fermées</p>
                  <p className="text-sm text-rose-600 mt-1">Les précommandes pour cette extension ne sont plus acceptées.</p>
                </div>
              )}

              {ext.product_type === 'bd_manga' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white border border-rose-200 rounded-2xl p-6 mt-4" data-testid="wishlist-section">
                  <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
                    <Heart className="h-5 w-5 text-rose-500" /> Ajouter à ma wishlist
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">Recevez une alerte quand cette sortie sera disponible</p>
                  {wishlistAdded ? (
                    <div className="mt-4 p-3 bg-rose-50 rounded-lg text-sm text-rose-700 font-medium flex items-center gap-2">
                      <Heart className="h-4 w-4 fill-rose-500 text-rose-500" /> Ajouté à votre wishlist !
                    </div>
                  ) : (
                    <div className="mt-4 flex gap-2">
                      <Input
                        type="email"
                        placeholder="votre@email.com"
                        value={wishlistEmail}
                        onChange={e => setWishlistEmail(e.target.value)}
                        className="flex-1 rounded-lg"
                        data-testid="wishlist-email-ext"
                      />
                      <Button
                        className="rounded-full bg-rose-600 hover:bg-rose-700 text-white"
                        onClick={async () => {
                          if (!wishlistEmail) { toast.error("Entrez votre email"); return; }
                          try {
                            await api.post('/wishlist', { email: wishlistEmail, extension_id: id });
                            localStorage.setItem('wishlist_email', wishlistEmail);
                            setWishlistAdded(true);
                            toast.success("Ajouté à votre wishlist !");
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Erreur");
                          }
                        }}
                        data-testid="add-wishlist-btn"
                      >
                        <Heart className="h-4 w-4 mr-1" /> Ajouter
                      </Button>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
